import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, HardHat, LogOut, Shield, X } from 'lucide-react';
import { useAuth } from '../../auth/AuthProviderUnified';
import logger from '../../utils/logger';
import { filterSettingsSections } from './settingsSections';
import './SettingsDrawer.css';

function SettingsDrawer({
  isOpen,
  onClose,
  role,
  getRoutePath = (path) => path,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const sections = useMemo(() => filterSettingsSections(role), [role]);
  const onCommandCenter = location.pathname.startsWith('/command-center');

  const handleNavigate = (path) => {
    navigate(getRoutePath(path));
    onClose();
  };

  const handleLogout = async () => {
    try {
      onClose();
      await logout();
      navigate('/login');
    } catch (err) {
      logger.error('[SettingsDrawer] Logout failed', { error: err.message });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="settings-drawer-overlay"
            data-testid="settings-drawer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="settings-drawer"
            data-testid="settings-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            role="dialog"
            aria-label="Settings"
          >
            <div className="settings-drawer-header">
              <h3>Settings</h3>
              <button
                type="button"
                className="settings-drawer-close"
                onClick={onClose}
                aria-label="Close settings"
              >
                <X size={18} />
              </button>
            </div>
            <div className="settings-drawer-content">
              {sections.map((section) => (
                <div
                  key={section.title}
                  className="settings-drawer-section"
                  data-testid={`settings-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="settings-drawer-section-title">{section.title}</span>
                  <div className="settings-drawer-links">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          type="button"
                          className="settings-drawer-link"
                          data-testid={`settings-link-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
                          onClick={() => handleNavigate(item.path)}
                        >
                          <Icon size={18} />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="settings-drawer-section" data-testid="settings-section-dashboard-view">
                <span className="settings-drawer-section-title">Dashboard View</span>
                <div className="settings-drawer-view-options">
                  <button
                    type="button"
                    className={`settings-drawer-view-option ${onCommandCenter ? 'active' : ''}`}
                    data-testid="settings-dashboard-view-command-center"
                    onClick={() => handleNavigate('/command-center')}
                  >
                    <div className="settings-drawer-view-option-icon">
                      <Brain size={20} />
                    </div>
                    <div className="settings-drawer-view-option-text">
                      <h4>Command Center</h4>
                      <p>AI-powered safety overview</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="settings-drawer-view-option"
                    data-testid="settings-dashboard-view-supervisor"
                    onClick={() => handleNavigate('/command-center')}
                  >
                    <div className="settings-drawer-view-option-icon">
                      <Shield size={20} />
                    </div>
                    <div className="settings-drawer-view-option-text">
                      <h4>Field Supervisor</h4>
                      <p>On-site operations view</p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="settings-drawer-section" data-testid="settings-section-role-dashboards">
                <span className="settings-drawer-section-title">Role Dashboards</span>
                <div className="settings-drawer-links">
                  <button
                    type="button"
                    className="settings-drawer-link"
                    data-testid="settings-link-foreman"
                    onClick={() => handleNavigate('/foreman')}
                  >
                    <HardHat size={18} />
                    <span>Foreman Dashboard</span>
                  </button>
                </div>
              </div>

              <div className="settings-drawer-section settings-drawer-section-logout">
                <div className="settings-drawer-links">
                  <button
                    type="button"
                    className="settings-drawer-link settings-drawer-link-logout"
                    data-testid="settings-link-logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={18} />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

SettingsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  role: PropTypes.string,
  getRoutePath: PropTypes.func,
};

export default SettingsDrawer;
